import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'

// Submissions are de-duplicated across controller instances. A single user
// gesture can reach more than one connected instance bound to the same field
// (e.g. when Turbo reconnects the controller), which would fire N identical
// create/update requests. This module-level lock, keyed by the field's dom
// prefix, lets the first submission win and turns the rest into no-ops.
// Same module-level-state-keyed-by-id convention as `openKeepAliveDropdownIds`
// in dropdown_floating_controller.js, for the same Turbo morph/reconnect reason.
const inFlightSubmits = new Set()

// Widget-specific orchestration for the partnership type selector.
// Positioning, open/close and click-outside are delegated to the canonical
// `dropdown-floating` controller (reached through an outlet); this controller
// only owns the trigger/search swap, client-side filtering, selection and the
// inline create/edit flow.
export default class extends Controller {
  static outlets = ['dropdown-floating']

  static targets = [
    'trigger',
    'searchWrapper',
    'search',
    'option',
    'createRow',
    'createLabel',
    'listView',
    'editView',
    'renameInput',
    'swatch',
    'input',
    'display',
    'error'
  ]

  static values = {
    teamSqid: String,
    fieldName: String,
    domPrefix: String,
    errorMessage: String,
    defaultColor: String,
    createUrl: String,
    paramName: String
  }

  static classes = ['active']

  connect () {
    this.editSqid = null
    this.editColor = null
    this.editName = null
  }

  onOpen () {
    this.clearError()
    this.showList()
    if (this.hasTriggerTarget) this.triggerTarget.hidden = true
    if (this.hasSearchWrapperTarget) this.searchWrapperTarget.hidden = false

    if (this.hasSearchTarget) {
      this.searchTarget.value = ''
      this.filter()
      this.searchTarget.focus()
    }
  }

  onClose () {
    if (this.hasSearchWrapperTarget) this.searchWrapperTarget.hidden = true
    if (this.hasTriggerTarget) this.triggerTarget.hidden = false
    this.showList()
  }

  close () {
    if (this.hasDropdownFloatingOutlet) this.dropdownFloatingOutlet.close()
  }

  showList () {
    if (this.hasEditViewTarget) this.editViewTarget.hidden = true
    if (this.hasListViewTarget) this.listViewTarget.hidden = false
  }

  filter () {
    const query = (this.hasSearchTarget ? this.searchTarget.value : '').trim().toLowerCase()
    let exactMatch = false

    this.optionTargets.forEach((option) => {
      const name = option.dataset.name || ''
      option.hidden = !name.includes(query)
      if (name === query) exactMatch = true
    })

    if (this.hasCreateRowTarget) {
      const show = query.length > 0 && !exactMatch
      this.createRowTarget.hidden = !show
      if (show && this.hasCreateLabelTarget) {
        this.createLabelTarget.textContent = this.searchTarget.value.trim()
      }
    }
  }

  preventSubmit (event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    if (this.hasCreateRowTarget && !this.createRowTarget.hidden) this.create()
  }

  select (event) {
    const chip = event.currentTarget.querySelector('.js-pt-chip')

    if (this.hasInputTarget) this.inputTarget.value = event.params.value
    if (chip && this.hasDisplayTarget) this.displayTarget.innerHTML = chip.outerHTML

    this.close()
  }

  async create () {
    const name = this.searchTarget.value.trim()
    if (!name) return

    this.clearError()
    const body = this.buildBody(name, this.defaultColorValue)
    const ok = await this.submit(this.createUrlValue, 'POST', body)
    if (ok) this.close()
  }

  edit (event) {
    event.preventDefault()
    event.stopPropagation()

    this.editSqid = event.params.sqid
    this.editColor = event.params.color
    this.editName = event.params.name
    if (this.hasRenameInputTarget) this.renameInputTarget.value = event.params.name
    this.highlightSwatch()

    if (this.hasListViewTarget) this.listViewTarget.hidden = true
    if (this.hasEditViewTarget) this.editViewTarget.hidden = false
    if (this.hasRenameInputTarget) this.renameInputTarget.focus()
  }

  highlightSwatch () {
    this.swatchTargets.forEach((swatch) => {
      swatch.classList.toggle(this.activeClass, swatch.dataset.color === this.editColor)
    })
  }

  pickColor (event) {
    this.editColor = event.params.color
    this.highlightSwatch()
    this.submitEdit()
  }

  commitRename (event) {
    if (event.type === 'keydown') {
      if (event.key !== 'Enter') return
      event.preventDefault()
      this.submitEdit({ returnToList: true })
      return
    }

    const name = this.hasRenameInputTarget ? this.renameInputTarget.value.trim() : ''
    if (name && name !== this.editName) this.submitEdit()
  }

  async submitEdit ({ returnToList = false } = {}) {
    if (!this.editSqid) return

    const name = this.hasRenameInputTarget ? this.renameInputTarget.value.trim() : ''
    if (!name) return

    this.clearError()
    const body = this.buildBody(name, this.editColor)
    const ok = await this.submit(`${this.createUrlValue}/${this.editSqid}`, 'PATCH', body)
    if (!ok) return

    this.editName = name
    if (returnToList) this.showList()
  }

  buildBody (name, color) {
    const body = new FormData()
    body.append('team_id', this.teamSqidValue)
    body.append('field_name', this.fieldNameValue)
    body.append('dom_prefix', this.domPrefixValue)
    body.append('selected_id', this.selectedId())
    body.append(`${this.paramNameValue}[name]`, name)
    body.append(`${this.paramNameValue}[color]`, color)
    return body
  }

  selectedId () {
    return this.hasInputTarget ? this.inputTarget.value : ''
  }

  async submit (url, method, body) {
    const lockKey = this.domPrefixValue || this.element.id
    if (inFlightSubmits.has(lockKey)) return false
    inFlightSubmits.add(lockKey)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        method,
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'text/vnd.turbo-stream.html'
        },
        body
      })

      if (!response.ok) {
        this.showError()
        return false
      }

      Turbo.renderStreamMessage(await response.text())
      this.filter()
      return true
    } finally {
      inFlightSubmits.delete(lockKey)
    }
  }

  showError () {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = this.errorMessageValue
    this.errorTarget.hidden = false
  }

  clearError () {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }
}
